import React, {useEffect, useState} from 'react'
import { 
  Table, 
  TableBody, 
  TableContainer, 
  TableRow, 
  Paper, 
  TextField,
  Autocomplete,
  Box,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableHead
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import Sidebar from './Sidebar';
import config from '../config';
import {
  PageContainer, 
  ContentContainer, 
  StyledTableCell, 
  Header, 
  StyledTableRow
} from './ContentContainer';

const ProjectionsList = ({ isPomList = false }) => {
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  
  // Filter state
  const [selectedRetailers, setSelectedRetailers] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedProductTypes, setSelectedProductTypes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [lockFilter, setLockFilter] = useState('All');

  // Unique filter options
  const [retailerOptions, setRetailerOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [productTypeOptions, setProductTypeOptions] = useState([]);

  // Pagination state
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);

  // A product is "locked" for this list based on the render target it shows (VTO vs POM)
  const isProductLocked = (session) => (isPomList ? session.lockPom : session.lockVto);

  useEffect(() => {
    fetchSessions().then((response) => {
      const sessions = response.products;
      setSessions(sessions);
      console.log(sessions);
      
      // Generate unique filter options
      const retailers = [...new Set(sessions.map(session => session.retailer))];
      const statuses = [...new Set(sessions.map(session => getRenderStatusText(session.status)))];
      const productTypes = [...new Set(sessions.map(session => session.categoryName))];

      setRetailerOptions(retailers);
      setStatusOptions(statuses);
      setProductTypeOptions(productTypes);
      setFilteredSessions(sessions);
      setIsLoading(false);
    }).catch((error) => {
      console.error(error);
      setIsLoading(false);
    });
  }, []);

  // Filter logic
  useEffect(() => {
    console.log('Filtering with:', {
      selectedRetailers,
      selectedStatuses,
      selectedProductTypes,
      searchQuery,
      totalSessions: sessions.length
    });

    const filtered = sessions.filter(session => {
      const retailerMatch = selectedRetailers.length === 0 || 
        selectedRetailers.includes(session.retailer);
      
      // Convert numeric status to text for filtering
      const sessionStatus = getRenderStatusText(session.status);
      const statusMatch = selectedStatuses.length === 0 || 
        selectedStatuses.includes(sessionStatus);
      
      const productTypeMatch = selectedProductTypes.length === 0 || 
        selectedProductTypes.includes(session.categoryName);
      
      // Safe search query match (case-insensitive) for product title or ID
      const searchMatch = !searchQuery ||
        (session.productName && session.productName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (session.productId && String(session.productId).toLowerCase().includes(searchQuery.toLowerCase()));

      const lockMatch = lockFilter === 'All' ||
        (lockFilter === 'Locked' ? isProductLocked(session) : !isProductLocked(session));

      return retailerMatch && statusMatch && productTypeMatch && searchMatch && lockMatch;
    });

    // Sort the filtered results
    const sorted = [...filtered].sort((a, b) => {
      // First sort by status - Rerender Requests always on top
      const statusOrder = {
        4: 0, // Rerender Request - highest priority
        0: 1, // Rendering
        3: 2, // Waiting
        1: 3, // Error
        2: 4  // Done
      };
      
      const statusA = statusOrder[a.status] ?? 2;
      const statusB = statusOrder[b.status] ?? 2;
      
      if (statusA !== statusB) {
        return statusA - statusB;
      }
      
      // If status is the same, sort by priority (higher priority first)
      return (b.priority ?? 0) - (a.priority ?? 0);
    });

    console.log('Filtered and sorted results:', {
      filteredCount: sorted.length,
      firstFewItems: sorted.slice(0, 3)
    });

    setFilteredSessions(sorted);
  }, [selectedRetailers, selectedStatuses, selectedProductTypes, searchQuery, lockFilter, sessions]);

  const fetchSessions = async () => {
    const response = await fetch(`${config.serverUrl}/models?pom_models=${isPomList}`);
    if (!response.ok){
      throw new Error('Failed to fetch sessions');
    }

    return await response.json();
  }

  const navigateToReviewPage = ({sessionId}) => {
    navigate(`/projections/${sessionId}`);
  }

  const handlePriorityChange = async (productId, size, currentPriority, priorityChange) => {
    try {
      const newPriority = currentPriority + priorityChange;
      const response = await axios.post(`${config.serverUrl}/models/priority`, {
        product_id: parseInt(productId, 10),
        size: parseInt(size, 10),
        priority: parseInt(newPriority, 10)
      });

      console.log('Priority updated:', response.data);

      setFilteredSessions((prevSessions) =>
        prevSessions.map((session) => {
          if (session.productId === productId && session.size === size) {
            return { ...session, priority: newPriority };
          }
          return session;
        })
      );
    } catch (error) {
      console.error('Failed to update priority:', error.response?.data || error.message);
    }
  };

  const FilterAutocomplete = ({
    label, 
    options, 
    selectedValues, 
    setSelectedValues
  }) => (
    <Autocomplete
      multiple
      options={options}
      value={selectedValues}
      onChange={(_, newValue) => setSelectedValues(newValue)}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip 
            {...getTagProps({ index })} 
            key={option} 
            label={option} 
            size="small"
            sx={{ 
              bgcolor: 'primary.light', 
              color: 'primary.contrastText' 
            }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField 
          {...params} 
          label={label} 
          variant="outlined" 
          size="small"
        />
      )}
      sx={{ minWidth: 200, mr: 2 }}
    />
  );

  const getRenderStatusText = (status) => {
    switch (status) {
      case 0:
        return 'Rendering';
      case 1:
        return 'Error';
      case 2:
        return 'Done';
      case 4:
        return 'Rerender Request';
      default:
        return 'Waiting';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 0:
        return '#FFF3E0'; // Light orange for waiting
      case 1:
        return '#FFEBEE'; // Light red for error
      case 2:
        return '#E8F5E9'; // Light green for done
      case 4:
        return '#F3E5F5'; // Light purple for rerender request
      default:
        return '#E3F2FD'; // Light blue for rendering
    }
  };

  const LOCKED_ROW_COLOR = '#FFCC80'; // Amber, distinct from all status colors, marks locked products

  // Add debug information for pagination
  const paginatedSessions = filteredSessions.slice(0, itemsPerPage);

  return (
    <PageContainer>
        <Sidebar navigate={navigate} />
        <ContentContainer>
          <Header>{isPomList ? 'POM Projections' : 'Projections'}</Header>
          
          {/* Filter Section - Hidden while loading */}
          {!isLoading && (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2, 
                flexWrap: 'wrap' 
              }}
            >
              <TextField
                label="Search Product"
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ minWidth: 200, mr: 2 }}
              />
              <FilterAutocomplete 
                label="Retailer" 
                options={retailerOptions} 
                selectedValues={selectedRetailers}
                setSelectedValues={setSelectedRetailers}
              />
              <FilterAutocomplete 
                label="Status" 
                options={statusOptions} 
                selectedValues={selectedStatuses}
                setSelectedValues={setSelectedStatuses}
              />
              <FilterAutocomplete
                label="Product Type"
                options={productTypeOptions}
                selectedValues={selectedProductTypes}
                setSelectedValues={setSelectedProductTypes}
              />
              <FormControl sx={{ minWidth: 140, mr: 2 }}>
                <InputLabel>Lock Status</InputLabel>
                <Select
                  value={lockFilter}
                  label="Lock Status"
                  onChange={(e) => setLockFilter(e.target.value)}
                  size="small"
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Locked">Locked</MenuItem>
                  <MenuItem value="Unlocked">Unlocked</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 120, mr: 2 }}>
                <InputLabel>Items</InputLabel>
                <Select
                  value={itemsPerPage}
                  label="Items"
                  onChange={(e) => setItemsPerPage(e.target.value)}
                  size="small"
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={filteredSessions.length}>All</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="Rendering Sessions Table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Image</StyledTableCell>
                  <StyledTableCell>Product ID (Admin link)</StyledTableCell>
                  <StyledTableCell>Product projections</StyledTableCell>
                  <StyledTableCell>Size</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Retailer</StyledTableCell>
                  <StyledTableCell>Product Type</StyledTableCell>
                  <StyledTableCell>Priority</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedSessions.map((session, index) => {
                  const locked = isProductLocked(session);
                  const rowColor = locked ? LOCKED_ROW_COLOR : getStatusColor(session.status);
                  return (
                  <StyledTableRow
                    key={`${session.productId}-${session.size}-${index}`}
                    onClick={() => navigateToReviewPage({sessionId: session.productId})}
                    sx={{
                      backgroundColor: rowColor,
                      '&:hover': {
                        backgroundColor: `${rowColor}CC`,
                      }
                    }}
                  >
                    <StyledTableCell component="th" scope="row">
                      <img src={session.imageUrl} style={{width: '50px'}} />
                    </StyledTableCell>

                    <StyledTableCell component="th" scope="row">
                      <a href={`https://yrulermgr.triplenext.net/Admin/CompareBag/EditBag/${session.productId}`}>{session.productId}</a>
                    </StyledTableCell>

                    <StyledTableCell>
                      <a href={`/projections/${session.productId}`}>{session.productName}</a>
                    </StyledTableCell>
                    <StyledTableCell>
                      {session.size}
                    </StyledTableCell>
                    <StyledTableCell>
                      {getRenderStatusText(session.status)}
                      {locked && (
                        <Chip
                          label="🔒 Locked"
                          size="small"
                          sx={{ ml: 1, bgcolor: LOCKED_ROW_COLOR }}
                        />
                      )}
                    </StyledTableCell>

                    <StyledTableCell>
                      {session.retailer}
                    </StyledTableCell>
                    <StyledTableCell>
                      {session.categoryName === "Watches" ? "⌚ Watch" : session.categoryName}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePriorityChange(session.productId, session.size, session.priority, -100);
                          }}
                        >
                          -100
                        </button>
                        {session.priority}
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePriorityChange(session.productId, session.size, session.priority, 100);
                          }}
                        >
                          +100
                        </button>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </ContentContainer>
    </PageContainer>
  )
}

export default ProjectionsList; 